import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ScanBarcode, Search, Plus, Minus, Trash2, Printer,
  Package, Settings2, ChevronDown,
} from 'lucide-react';
import { BarcodeDisplay } from '@/components/ui/barcode-display';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { cn, formatCurrency } from '@/lib/utils';
import {
  clampCopies, computeLayout, pageCount, PAGE_PRESETS,
  MAX_COPIES_PER_LABEL, MAX_TOTAL_LABELS,
} from './label-sheet';

// ── Types ──────────────────────────────────────────────────────────────────

interface Product {
  id: string;
  name: string;
  sku: string;
  barcode?: string;
  salePrice: number;
  category?: { name: string };
}

interface PrintQueueItem {
  product: Product;
  quantity: number;
}

type LabelSize = 'small' | 'medium' | 'large';

interface LabelConfig {
  size: LabelSize;
  label: string;
  widthMm: number;
  heightMm: number;
  widthPx: number;
  heightPx: number;
}

const LABEL_CONFIGS: LabelConfig[] = [
  { size: 'small',  label: 'Small (38×25mm)',   widthMm: 38,  heightMm: 25,  widthPx: 144, heightPx: 94  },
  { size: 'medium', label: 'Medium (58×40mm)',  widthMm: 58,  heightMm: 40,  widthPx: 219, heightPx: 151 },
  { size: 'large',  label: 'Large (100×60mm)',  widthMm: 100, heightMm: 60,  widthPx: 378, heightPx: 227 },
];

// ── Label Component ────────────────────────────────────────────────────────

interface LabelProps {
  product: Product;
  config: LabelConfig;
  showPrice: boolean;
  showName: boolean;
  showSku: boolean;
  forPrint?: boolean;
}

function BarcodeLabel({ product, config, showPrice, showName, showSku, forPrint }: LabelProps) {
  const barcodeValue = product.barcode || product.sku;
  const isSmall = config.size === 'small';
  const isMedium = config.size === 'medium';

  return (
    <div
      className={cn(
        'border border-border bg-background flex flex-col items-center justify-center overflow-hidden',
        forPrint ? 'print-label' : '',
      )}
      style={{
        width: forPrint ? `${config.widthMm}mm` : config.widthPx,
        height: forPrint ? `${config.heightMm}mm` : config.heightPx,
        padding: forPrint ? '1.5mm' : isSmall ? '4px' : '6px',
        boxSizing: 'border-box',
      }}
    >
      {showName && (
        <p
          className="font-bold text-foreground leading-none text-center truncate w-full"
          style={{ fontSize: isSmall ? '5px' : isMedium ? '7px' : '9px', marginBottom: '1px' }}
        >
          {product.name}
        </p>
      )}

      <BarcodeDisplay
        value={barcodeValue}
        width={isSmall ? 0.8 : isMedium ? 1.1 : 1.5}
        height={isSmall ? 22 : isMedium ? 36 : 54}
        displayValue={true}
        className="flex-1 flex items-center justify-center"
      />

      <div className="flex items-center gap-2 w-full justify-center">
        {showSku && (
          <p
            className="text-muted-foreground text-center font-mono"
            style={{ fontSize: isSmall ? '4.5px' : isMedium ? '6px' : '7px' }}
          >
            {product.sku}
          </p>
        )}
        {showPrice && (
          <p
            className="font-bold text-foreground text-center"
            style={{ fontSize: isSmall ? '5.5px' : isMedium ? '7.5px' : '9px' }}
          >
            {formatCurrency(Number(product.salePrice))}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export function BarcodeLabelsPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [queue, setQueue] = useState<PrintQueueItem[]>([]);
  const [labelSize, setLabelSize] = useState<LabelSize>('medium');
  const [showPrice, setShowPrice] = useState(true);
  const [showName, setShowName] = useState(true);
  const [showSku, setShowSku] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [pageId, setPageId] = useState('roll');

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const config = LABEL_CONFIGS.find((c) => c.size === labelSize)!;

  // Debounce search
  const handleSearch = (val: string) => {
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(val), 350);
  };

  const { data, isFetching } = useQuery<{ data: Product[] }>({
    queryKey: ['products-for-labels', debouncedSearch],
    queryFn: () =>
      api
        .get('/products', { params: { search: debouncedSearch || undefined, limit: 30, page: 1 } })
        .then((r) => r.data),
    enabled: true,
    staleTime: 30_000,
  });

  const products = data?.data ?? [];

  const totalOf = (items: PrintQueueItem[], exceptId?: string) =>
    items.reduce((sum, i) => (i.product.id === exceptId ? sum : sum + i.quantity), 0);

  const setQty = (id: string, value: number) => {
    if (isNaN(value) || value < 0) return;
    const { value: next, message } = clampCopies(value, totalOf(queue, id));
    if (message) toast.error(message, { id: 'label-copies-cap' });
    setQueue((prev) =>
      next === 0
        ? prev.filter((i) => i.product.id !== id)
        : prev.map((i) => i.product.id === id ? { ...i, quantity: next } : i),
    );
  };

  const addToQueue = (product: Product) => {
    const existing = queue.find((i) => i.product.id === product.id);
    if (existing) {
      setQty(product.id, existing.quantity + 1);
      return;
    }
    const { value, message } = clampCopies(1, totalOf(queue));
    if (message) {
      toast.error(message, { id: 'label-copies-cap' });
      return;
    }
    setQueue((prev) => [...prev, { product, quantity: value }]);
  };

  const updateQty = (id: string, delta: number) => {
    const current = queue.find((i) => i.product.id === id);
    if (current) setQty(id, Math.max(0, current.quantity + delta));
  };

  const removeFromQueue = (id: string) => {
    setQueue((prev) => prev.filter((i) => i.product.id !== id));
  };

  // Expand queue to individual labels for printing
  const printItems = useMemo(
    () => !printing ? [] : queue.flatMap((item) => Array(item.quantity).fill(item.product)),
    [queue, printing],
  );

  const totalLabels = queue.reduce((sum, i) => sum + i.quantity, 0);
  const preset = PAGE_PRESETS.find((x) => x.id === pageId) ?? PAGE_PRESETS[0];
  const layout = computeLayout(preset, config.widthMm, config.heightMm);
  const pages = pageCount(totalLabels, layout.perPage);

  const handlePrint = () => {
    if (queue.length === 0) {
      toast.error('Add products to the print queue first');
      return;
    }
    setPrinting(true);
  };

  // The print sheet is mounted only while printing, so large queues never render in the page.
  useEffect(() => {
    if (!printing) return;
    const t = setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 50);
    return () => clearTimeout(t);
  }, [printing]);

  return (
    <>
      {/* ── Print Stylesheet (injected via <style> tag in head) ──────────── */}
      <style>{`
        @page { size: ${layout.pageCss}; margin: ${layout.marginMm}mm; }
        @media print {
          body > *:not(#barcode-print-area) { display: none !important; }
          #barcode-print-area {
            display: grid !important;
            grid-template-columns: repeat(${layout.cols}, ${layout.cellWidthMm}mm);
            grid-auto-rows: ${layout.cellHeightMm}mm;
            gap: ${layout.gapMm}mm;
            align-content: start;
            padding: 0;
          }
          #barcode-print-area .label-cell {
            width: ${layout.cellWidthMm}mm;
            height: ${layout.cellHeightMm}mm;
            overflow: hidden;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          #barcode-print-area .label-cell > div {
            transform: scale(${layout.scale});
            transform-origin: top left;
          }
          #barcode-print-area * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        #barcode-print-area { display: none; }
      `}</style>

      {/* ── Print Area — portal to document.body so CSS can isolate it ───── */}
      {createPortal(
        <div id="barcode-print-area">
          {printItems.map((product, idx) => (
            <div key={idx} className="label-cell">
              <BarcodeLabel
                product={product}
                config={config}
                showPrice={showPrice}
                showName={showName}
                showSku={showSku}
                forPrint
              />
            </div>
          ))}
        </div>,
        document.body,
      )}

      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <PageHeader
        title="Barcode Labels"
        subtitle="Search products, build a print queue, and print labels for your barcode printer."
        actions={
          <Button
            onClick={handlePrint}
            disabled={queue.length === 0}
            variant="gradient"
            className="flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            Print {totalLabels > 0 ? `${totalLabels} ${totalLabels === 1 ? 'Label' : 'Labels'}` : 'Labels'}
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* ── Left: Product Search ────────────────────────────────────────── */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Search Products</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute start-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="ps-9"
                  placeholder="Search by name, SKU or barcode…"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>

              <div className="max-h-[480px] overflow-y-auto space-y-1 pe-1">
                {isFetching && (
                  <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
                )}
                {!isFetching && products.length === 0 && (
                  <div className="py-12 text-center">
                    <Package className="mx-auto h-8 w-8 text-muted-foreground/40" />
                    <p className="mt-2 text-sm text-muted-foreground">No products found</p>
                  </div>
                )}
                {products.map((product) => {
                  const inQueue = queue.some((i) => i.product.id === product.id);
                  return (
                    <div
                      key={product.id}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors cursor-pointer hover:bg-accent/50',
                        inQueue && 'border-primary/30 bg-primary/10',
                      )}
                      onClick={() => addToQueue(product)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs font-mono text-muted-foreground">{product.sku}</span>
                          {product.barcode && (
                            <span className="flex items-center gap-0.5 text-xs text-muted-foreground/60">
                              <ScanBarcode className="h-3 w-3" />{product.barcode}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-end shrink-0">
                        <p className="text-xs font-medium text-foreground">
                          {formatCurrency(Number(product.salePrice))}
                        </p>
                        {product.category && (
                          <p className="text-xs text-muted-foreground">{product.category.name}</p>
                        )}
                      </div>
                      <button
                        className={cn(
                          'rounded-full p-1 transition-colors shrink-0',
                          inQueue
                            ? 'bg-primary/10 text-primary-text hover:bg-primary/20'
                            : 'bg-muted hover:bg-accent text-muted-foreground',
                        )}
                        onClick={(e) => { e.stopPropagation(); addToQueue(product); }}
                        title="Add to queue"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Right: Print Queue + Settings ─────────────────────────────── */}
        <div className="space-y-4">

          {/* Label Settings */}
          <Card>
            <CardHeader className="pb-2">
              <button
                className="flex w-full items-center justify-between"
                onClick={() => setShowSettings((s) => !s)}
              >
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings2 className="h-4 w-4" /> Label Settings
                </CardTitle>
                <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', showSettings && 'rotate-180')} />
              </button>
            </CardHeader>
            {showSettings && (
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Label Size</Label>
                  <div className="flex gap-2 flex-wrap">
                    {LABEL_CONFIGS.map((c) => (
                      <button
                        key={c.size}
                        onClick={() => setLabelSize(c.size)}
                        className={cn(
                          'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                          labelSize === c.size
                            ? 'border-primary bg-primary/10 text-primary-text'
                            : 'border-border text-muted-foreground hover:bg-accent',
                        )}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Page Size</Label>
                  <div className="flex gap-2 flex-wrap">
                    {PAGE_PRESETS.map((pp) => (
                      <button
                        key={pp.id}
                        onClick={() => setPageId(pp.id)}
                        className={cn(
                          'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                          pageId === pp.id
                            ? 'border-primary bg-primary/10 text-primary-text'
                            : 'border-border text-muted-foreground hover:bg-accent',
                        )}
                      >
                        {pp.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {layout.perPage === 1 && preset.kind === 'roll'
                      ? `Each label prints on its own ${config.widthMm}×${config.heightMm}mm page.`
                      : `${layout.cols} × ${layout.rows} = ${layout.perPage} labels per page.`}
                    {layout.scale < 1 ? ' Labels are scaled down to fit the sheet cells.' : ''}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Show on Label</Label>
                  <div className="flex gap-3">
                    {[
                      { key: 'name', label: 'Product Name', value: showName, set: setShowName },
                      { key: 'price', label: 'Price', value: showPrice, set: setShowPrice },
                      { key: 'sku', label: 'SKU', value: showSku, set: setShowSku },
                    ].map(({ key, label, value, set }) => (
                      <button
                        key={key}
                        onClick={() => set((v) => !v)}
                        className={cn(
                          'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                          value
                            ? 'border-success bg-success/10 text-success-text'
                            : 'border-border text-muted-foreground hover:bg-accent',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Live Preview */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Label Preview</Label>
                  <div className="flex items-center justify-center rounded-xl border bg-muted/30 py-6">
                    <BarcodeLabel
                      product={{
                        id: 'preview',
                        name: 'Sample Product',
                        sku: 'SKU-001',
                        barcode: '1234567890',
                        salePrice: 299,
                      }}
                      config={config}
                      showPrice={showPrice}
                      showName={showName}
                      showSku={showSku}
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Print Queue */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  Print Queue {queue.length > 0 && (
                    <span className="ms-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary-text">
                      {totalLabels} {totalLabels === 1 ? 'label' : 'labels'}
                    </span>
                  )}
                </CardTitle>
                {queue.length > 0 && (
                  <button
                    className="text-xs text-muted-foreground hover:text-destructive-text transition-colors"
                    onClick={() => setQueue([])}
                  >
                    Clear all
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {queue.length === 0 ? (
                <div className="py-12 text-center">
                  <ScanBarcode className="mx-auto h-8 w-8 text-muted-foreground/30" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    Click products on the left to add them here
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[480px] overflow-y-auto pe-1">
                  {queue.map(({ product, quantity }) => (
                    <div
                      key={product.id}
                      className="rounded-lg border bg-card"
                    >
                      {/* Barcode preview */}
                      <div className="flex items-center justify-center bg-background rounded-t-lg border-b py-2">
                        <BarcodeLabel
                          product={product}
                          config={config}
                          showPrice={showPrice}
                          showName={showName}
                          showSku={showSku}
                        />
                      </div>
                      {/* Controls row */}
                      <div className="flex items-center gap-3 px-3 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate text-foreground">{product.name}</p>
                          <p className="text-xs font-mono text-muted-foreground">{product.barcode || product.sku}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            className="rounded border p-0.5 hover:bg-accent transition-colors"
                            onClick={() => updateQty(product.id, -1)}
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <Input
                            type="number"
                            min={1}
                            max={MAX_COPIES_PER_LABEL}
                            value={quantity}
                            onChange={(e) => setQty(product.id, parseInt(e.target.value) || 0)}
                            className="w-10 h-auto rounded px-0.5 text-center py-0.5 shadow-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                          <button
                            className="rounded border p-0.5 hover:bg-accent transition-colors"
                            onClick={() => updateQty(product.id, 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <button
                          className="text-muted-foreground hover:text-destructive-text transition-colors shrink-0"
                          onClick={() => removeFromQueue(product.id)}
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {queue.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <Button
                    onClick={handlePrint}
                    variant="gradient"
                    className="w-full flex items-center gap-2"
                  >
                    <Printer className="h-4 w-4" />
                    Print {totalLabels} Label{totalLabels !== 1 ? 's' : ''} ({pages} page{pages !== 1 ? 's' : ''})
                  </Button>
                  <p className="mt-2 text-center text-xs text-muted-foreground">
                    Select your barcode printer in the print dialog. Up to {MAX_COPIES_PER_LABEL} copies per label and {MAX_TOTAL_LABELS} labels per print.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
